/**
 * @cafe-os/db — paymentConfig (Этап 11 polish, миграция 00100).
 *
 * Per-tenant delivery payment mode (cashier_only / kaspi_manual / kaspi_api).
 * Super-admin sets per-tenant via super.e-menu.bar UI. Guest checkout читает
 * через anon-callable get_tenant_payment_config.
 *
 * API ключ для kaspi_api хранится в Supabase Vault — RPC возвращает только
 * boolean `api_key_set`, raw value никогда не попадает в clients.
 */
import { callRpcWithDefaults } from "./errors";

export type PaymentMode = "cashier_only" | "kaspi_manual" | "kaspi_api";

/**
 * 00395: единственный Kaspi-вариант оплаты ДОСТАВКИ (отдельно от зала/брони).
 *   none     — без Kaspi (только наличные, если включены);
 *   transfer — перевод на номер кафе (нужен kaspi_phone);
 *   remote   — кассир шлёт запрос на номер гостя;
 *   auto     — Kaspi автомат (нужна активация платформой).
 */
export type DeliveryKaspiMethod = "none" | "transfer" | "remote" | "auto";

export interface TenantPaymentConfigPublic {
  mode: PaymentMode;
  kaspi_phone: string | null;
  /** 00179: имя получателя для гостевого экрана перевода («Получатель: Е*** А.»). */
  kaspi_holder_name: string | null;
  /** 00179: ЯВНЫЙ флаг dine-in Kaspi-сверки. kaspi_phone сам по себе фичу НЕ включает. */
  dine_in_transfer_enabled: boolean;
  /** 00184: ЯВНЫЙ флаг «Попросить удалённую оплату» (нужен Kaspi Pay терминал у кафе). */
  dine_in_remote_enabled: boolean;
  /** 00179: минут до авто-истечения pending-заявки (default 30). */
  confirm_timeout_minutes: number;
  /**
   * 00321: «Kaspi автомат» (3-й способ) — super-admin-управляемый флаг. Owner
   * включить не может (видит серым). Возвращается get_tenant_payment_config;
   * опционален, т.к. не все config-RPC его отдают (super_admin_get / owner_set).
   */
  dine_in_kaspi_auto_enabled?: boolean;
  /**
   * 00329: владелец ВЫБРАЛ «Kaspi автомат» как способ оплаты. Имеет силу только
   * вместе с dine_in_kaspi_auto_enabled (сервер гасит «залипший» выбор). Когда
   * true — гостю (доставка) показывается ТОЛЬКО Kaspi-авто.
   *
   * ⚠️ 00395: используется залом (касса) и бронью (PaymentScreen). Гостевой
   * чекаут ДОСТАВКИ больше НЕ читает dine_in_* — он читает delivery_* ниже.
   */
  dine_in_kaspi_auto_selected?: boolean;
  /**
   * 00395: наличные при получении для ДОСТАВКИ (COD) вкл/выкл. Default true.
   * Опционален — не все config-RPC отдают delivery_* (только get_tenant_payment_config).
   */
  delivery_cod_enabled?: boolean;
  /**
   * 00395: единственный Kaspi-вариант для ДОСТАВКИ. Сервер уже применяет гейты
   * (auto только при активации платформой, transfer только при заданном номере).
   */
  delivery_kaspi_method?: DeliveryKaspiMethod;
}

export interface TenantPaymentConfigFull extends TenantPaymentConfigPublic {
  api_key_set: boolean;
  /** 00327: заведён ли connection-пакет кафе в Vault (адрес+сессия). super-admin. */
  kaspi_auto_conn_set?: boolean;
}

const PUBLIC_CONFIG_FALLBACK: TenantPaymentConfigPublic = {
  mode: "cashier_only",
  kaspi_phone: null,
  kaspi_holder_name: null,
  dine_in_transfer_enabled: false,
  dine_in_remote_enabled: false,
  confirm_timeout_minutes: 30,
  dine_in_kaspi_auto_enabled: false,
  dine_in_kaspi_auto_selected: false,
  delivery_cod_enabled: true,
  delivery_kaspi_method: "none",
};

/** Anon-callable. Returns only mode + kaspi requisites, never raw API key. */
export async function getTenantPaymentConfig(
  tenantId: string,
): Promise<TenantPaymentConfigPublic> {
  const rows = await callRpcWithDefaults<TenantPaymentConfigPublic[]>(
    "get_tenant_payment_config",
    { p_tenant_id: tenantId },
  );
  const row = Array.isArray(rows) ? rows[0] : (rows as unknown as TenantPaymentConfigPublic);
  return row ?? PUBLIC_CONFIG_FALLBACK;
}

/** Super-admin only. Returns api_key_set boolean (never the raw key value). */
export async function superAdminGetPaymentConfig(
  tenantId: string,
): Promise<TenantPaymentConfigFull> {
  const rows = await callRpcWithDefaults<TenantPaymentConfigFull[]>(
    "super_admin_get_payment_config_full",
    { p_tenant_id: tenantId },
  );
  const row = Array.isArray(rows) ? rows[0] : (rows as unknown as TenantPaymentConfigFull);
  return row ?? { ...PUBLIC_CONFIG_FALLBACK, api_key_set: false, kaspi_auto_conn_set: false };
}

export interface SuperAdminSetPaymentConfigInput {
  tenantId: string;
  mode: PaymentMode;
  kaspiPhone: string | null;
  /**
   * Vault semantics:
   *   null  → keep existing secret (no Vault write)
   *   ''    → delete the secret
   *   value → upsert (rotate or create)
   */
  kaspiApiKey: string | null;
  /** 00179: null = не менять (сервер сохранит текущее значение). */
  kaspiHolderName?: string | null;
  /** 00179: null = не менять. Включение требует kaspi_phone (валидирует сервер). */
  dineInTransferEnabled?: boolean | null;
  /** 00184: null = не менять. «Попросить удалённую оплату» (Kaspi Pay терминал). */
  dineInRemoteEnabled?: boolean | null;
  /** 00179: null = не менять. Сервер валидирует 5..240. */
  confirmTimeoutMinutes?: number | null;
  /** 00321: null = не менять. «Kaspi автомат» (3-й способ) — только super-admin. */
  dineInKaspiAutoEnabled?: boolean | null;
}

/** Super-admin only. Writes config + manages Vault secret. Returns post-state. */
export async function superAdminSetPaymentConfig(
  input: SuperAdminSetPaymentConfigInput,
): Promise<TenantPaymentConfigFull> {
  const rows = await callRpcWithDefaults<TenantPaymentConfigFull[]>(
    "super_admin_set_payment_config",
    {
      p_tenant_id: input.tenantId,
      p_mode: input.mode,
      p_kaspi_phone: input.kaspiPhone,
      p_kaspi_api_key: input.kaspiApiKey,
      p_kaspi_holder_name: input.kaspiHolderName ?? null,
      p_dine_in_transfer_enabled: input.dineInTransferEnabled ?? null,
      p_confirm_timeout_minutes: input.confirmTimeoutMinutes ?? null,
      p_dine_in_remote_enabled: input.dineInRemoteEnabled ?? null,
      p_dine_in_kaspi_auto_enabled: input.dineInKaspiAutoEnabled ?? null,
    },
  );
  const row = Array.isArray(rows) ? rows[0] : (rows as unknown as TenantPaymentConfigFull);
  if (!row) throw new Error("[db/superAdminSetPaymentConfig] empty result");
  return row;
}

/** Режимы, доступные owner'у из кабинета (kaspi_api — только super-admin). */
export type OwnerPaymentMode = "cashier_only" | "kaspi_manual";

export interface OwnerSetPaymentConfigInput {
  mode: OwnerPaymentMode;
  /** Обязателен для kaspi_manual (KZ-телефон, 11 цифр — валидирует сервер). */
  kaspiPhone: string | null;
  /** Имя получателя для гостевого экрана перевода (опционально). */
  kaspiHolderName: string | null;
  /** 00205: dine-in «перевод на номер» (гость переводит на kaspiPhone). Требует телефон. */
  dineInTransferEnabled?: boolean;
  /** 00205: dine-in «удалёнка» (гость сам вводит свой номер, кассир шлёт запрос). */
  dineInRemoteEnabled?: boolean;
  /**
   * 00329: владелец выбрал «Kaspi автомат» эксклюзивно для гостя. Сервер примет
   * true только если dine_in_kaspi_auto_enabled (иначе kaspi_auto_not_enabled).
   */
  dineInKaspiAutoSelected?: boolean;
}

/**
 * Owner/manager/admin настраивает способ оплаты СВОЕГО тенанта из кабинета
 * (00193 + 00205). cashier_only | kaspi_manual + dine-in флаги (перевод на номер /
 * удалёнка). kaspi_api и Vault-ключи остаются доменом super-admin. Закрывает
 * onboarding-шаг «Платежи настроены».
 */
export async function ownerSetPaymentConfig(
  input: OwnerSetPaymentConfigInput,
): Promise<TenantPaymentConfigFull> {
  const rows = await callRpcWithDefaults<TenantPaymentConfigFull[]>(
    "owner_set_payment_config",
    {
      p_mode: input.mode,
      p_kaspi_phone: input.kaspiPhone,
      p_kaspi_holder_name: input.kaspiHolderName,
      p_dine_in_transfer_enabled: input.dineInTransferEnabled ?? null,
      p_dine_in_remote_enabled: input.dineInRemoteEnabled ?? null,
      p_dine_in_kaspi_auto_selected: input.dineInKaspiAutoSelected ?? null,
    },
  );
  const row = Array.isArray(rows) ? rows[0] : (rows as unknown as TenantPaymentConfigFull);
  if (!row) throw new Error("[db/ownerSetPaymentConfig] empty result");
  return row;
}

export interface OwnerSetDeliveryPaymentConfigInput {
  /** Наличные при получении для доставки (COD) вкл/выкл. */
  codEnabled: boolean;
  /** Один Kaspi-вариант для доставки (none/transfer/remote/auto). */
  kaspiMethod: DeliveryKaspiMethod;
}

export interface DeliveryPaymentConfigResult {
  delivery_cod_enabled: boolean;
  delivery_kaspi_method: DeliveryKaspiMethod;
}

/**
 * 00395: owner/manager/admin настраивает оплату ДОСТАВКИ отдельно от зала и брони.
 * Пишет только delivery_* ключи (RPC owner_set_delivery_payment_config) — dine_in_*
 * (зал/касса + бронь-депозит) остаются нетронутыми. Сервер валидирует:
 *   • auto — только при активации платформой (kaspi_auto_not_enabled);
 *   • transfer — только при заданном kaspi_phone (kaspi_phone_required_for_transfer);
 *   • нельзя выключить наличные И выбрать 'none' (delivery_needs_a_payment_method).
 */
export async function ownerSetDeliveryPaymentConfig(
  input: OwnerSetDeliveryPaymentConfigInput,
): Promise<DeliveryPaymentConfigResult> {
  const rows = await callRpcWithDefaults<DeliveryPaymentConfigResult[]>(
    "owner_set_delivery_payment_config",
    {
      p_delivery_cod_enabled: input.codEnabled,
      p_delivery_kaspi_method: input.kaspiMethod,
    },
  );
  const row = Array.isArray(rows) ? rows[0] : (rows as unknown as DeliveryPaymentConfigResult);
  if (!row) throw new Error("[db/ownerSetDeliveryPaymentConfig] empty result");
  return row;
}

/** Helper: Kazakhstan phone validation (+7XXXXXXXXXX or 8XXXXXXXXXX, 11 digits). */
export function isValidKaspiPhone(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  return digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"));
}
